import { httpsCallable } from "firebase/functions";
import { functions } from "../../../fb";
import {
  calculateDaysBetween,
  getComparisonRange,
  COMPARISON_PERIOD,
} from "@/store/utils/statisticsUtils";
import dayjs from "dayjs";

const defaultState = {
  range: {
    min: "2023-10-17T00:00:00+02:00",
    max: "2023-10-17T23:59:59+02:00",
  },
  statistic: null,
  comparisonStatistic: null,
  comparisonMode: COMPARISON_PERIOD,
  loading: false,
  comparisonLoading: false,
  refreshing: false,
  error: false,
  errorMessage: "",
};

const getters = {
  statistic: (state) => state.statistic,
  statisticComparison: (state) => state.comparisonStatistic,
  statisticComparisonMode: (state) => state.comparisonMode,
  statisticComparisonLoading: (state) => state.comparisonLoading,
  statisticComparisonRange: (state) =>
    getComparisonRange(state.range, state.comparisonMode),
  statisticRange: (state) => state.range,
  statisticLoading: (state) => state.loading,
  statisticRefreshing: (state) => state.refreshing,
  statisticError: (state) => state.error,
  statisticErrorMessage: (state) => state.errorMessage,
  statisticsDayRange: (state) => {
    return calculateDaysBetween(state.range.min, state.range.max);
  },
};

const actions = {
  async refreshStatistic({ commit }, selectedDate) {
    commit("setStatisticRefreshing", true);
    commit("setStatisticError", false);
    const currentLocalizedDate = dayjs(selectedDate);

    try {
      const syncStatistics = httpsCallable(functions, "syncStatistics");
      const res = await syncStatistics({
        created_at_min: currentLocalizedDate.startOf("day").format(),
        created_at_max: currentLocalizedDate.endOf("day").format(),
      });
      commit("setStatistic", res.data);
    } catch (e) {
      commit("setStatisticError", true);
      commit(
        "setStatisticErrorMessage",
        "There was an Error Loading the Statistics"
      );
    }
    commit("setStatisticRefreshing", false);
  },
  async loadStatistic({ commit, dispatch, getters }) {
    commit("setStatisticLoading", true);
    commit("setStatisticError", false);
    try {
      const getStatistics = httpsCallable(functions, "getSyncedStatistics");
      const res = await getStatistics({
        created_at_min: getters.statisticRange.min,
        created_at_max: getters.statisticRange.max,
      });
      commit("setStatistic", res.data);
    } catch (e) {
      commit("setStatisticError", true);
      commit(
        "setStatisticErrorMessage",
        "There was an Error Loading the Statistics"
      );
    }
    commit("setStatisticLoading", false);
    // Load the comparison period in the background (does not block the view).
    dispatch("loadComparisonStatistic");
  },
  async loadComparisonStatistic({ commit, getters }) {
    commit("setStatisticComparisonLoading", true);
    try {
      const getStatistics = httpsCallable(functions, "getSyncedStatistics");
      const comparisonRange = getters.statisticComparisonRange;
      const res = await getStatistics({
        created_at_min: comparisonRange.min,
        created_at_max: comparisonRange.max,
      });
      commit("setComparisonStatistic", res.data);
    } catch (e) {
      commit("setComparisonStatistic", null);
    }
    commit("setStatisticComparisonLoading", false);
  },
  updateComparisonMode({ commit, dispatch }, mode) {
    commit("setComparisonMode", mode);
    dispatch("loadComparisonStatistic");
  },
};

const mutations = {
  setStatisticLoading: (state, bool) => (state.loading = bool),
  setStatisticComparisonLoading: (state, bool) =>
    (state.comparisonLoading = bool),
  setStatisticRefreshing: (state, bool) => (state.refreshing = bool),
  setStatistic: (state, statistic) => (state.statistic = statistic),
  setComparisonStatistic: (state, statistic) =>
    (state.comparisonStatistic = statistic),
  setComparisonMode: (state, mode) => (state.comparisonMode = mode),
  setStatisticError: (state, bool) => (state.error = bool),
  setStatisticErrorMessage: (state, message) => (state.errorMessage = message),
  setStatisticRange: (state, range) => {
    state.range = {
      min: range.min,
      max: range.max,
    };
  },
};

const persisted = window.sessionStorage["taskDash"]
  ? JSON.parse(window.sessionStorage["taskDash"]).statistic
  : {};
const state = Object.assign({}, defaultState, persisted);

export default {
  state,
  getters,
  actions,
  mutations,
};
